import { createClient } from '@supabase/supabase-js';

// 从环境变量获取Supabase配置
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.SUPABASE_ANON_KEY;

// 创建Supabase客户端
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// 我们不需要重写URL方法，因为vite.config.js中的代理配置已经处理了跨域问题

/**
 * 生成随机nonce
 * @returns {string} 随机生成的nonce
 */
export const generateNonce = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/**
 * 获取或创建Web3认证记录
 * @param {string} network 网络类型 ('ethereum' 或 'solana')
 * @param {string} walletAddress 钱包地址
 * @returns {Promise<Object>} 包含nonce的认证记录
 */
export const getOrCreateWeb3Auth = async (network, walletAddress) => {
  try {
    // 查询现有记录
    const { data: existingAuth, error: queryError } = await supabase
      .from('web3_auth')
      .select('*')
      .eq('wallet_address', walletAddress)
      .single();

    if (queryError && queryError.code !== 'PGRST116') { // PGRST116 是"没有找到结果"的错误
      throw queryError;
    }

    // 如果找到现有记录，更新nonce
    if (existingAuth) {
      const nonce = generateNonce();
      const { data: updatedAuth, error: updateError } = await supabase
        .from('web3_auth')
        .update({ 
          nonce,
          network // 确保网络类型是最新的
        })
        .eq('id', existingAuth.id)
        .select()
        .single();

      if (updateError) throw updateError;
      return updatedAuth;
    }

    // 如果没有找到记录，创建新记录
    const nonce = generateNonce();
    const { data: newAuth, error: insertError } = await supabase
      .from('web3_auth')
      .insert([
        { 
          network, 
          wallet_address: walletAddress, 
          nonce 
        }
      ])
      .select()
      .single();

    if (insertError) throw insertError;
    return newAuth;
  } catch (error) {
    throw error;
  }
};

/**
 * 创建Web3会话
 * @param {string} network 网络类型 ('ethereum' 或 'solana')
 * @param {string} walletAddress 钱包地址
 * @param {string} signature 签名
 * @returns {Promise<Object>} 创建的会话记录
 */
export const createWeb3Session = async (network, walletAddress, signature) => {
  try {
    // 设置会话过期时间（例如7天后）
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    // 将所有现有会话设置为非活跃
    await supabase
      .from('web3_sessions')
      .update({ is_active: false })
      .eq('wallet_address', walletAddress);

    // 创建新会话
    const { data: session, error } = await supabase
      .from('web3_sessions')
      .insert([
        { 
          network, 
          wallet_address: walletAddress, 
          sign: signature,
          expires_at: expiresAt.toISOString(),
          is_active: true
        }
      ])
      .select()
      .single();

    if (error) throw error;

    // 更新auth表的last_signed_at
    await supabase
      .from('web3_auth')
      .update({ last_signed_at: new Date().toISOString() })
      .eq('wallet_address', walletAddress);

    return session;
  } catch (error) {
    throw error;
  }
};

/**
 * 验证Web3会话
 * @param {string} walletAddress 钱包地址
 * @returns {Promise<boolean>} 如果会话有效，则为true，否则为false
 */
export const verifyWeb3Session = async (walletAddress) => {
  try {
    const { data, error } = await supabase
      .from('web3_sessions')
      .select('*')
      .eq('wallet_address', walletAddress)
      .eq('is_active', true)
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error) return false;
    return !!data;
  } catch (error) {
    return false;
  }
};

/**
 * 使Web3会话失效
 * @param {string} walletAddress 钱包地址
 * @returns {Promise<void>}
 */
export const invalidateWeb3Session = async (walletAddress) => {
  try {
    await supabase
      .from('web3_sessions')
      .update({ is_active: false })
      .eq('wallet_address', walletAddress);
  } catch (error) {
    throw error;
  }
};

/**
 * 获取用户游戏次数信息
 * @param {string} userId 用户ID
 * @returns {Promise<Object>} 用户游戏次数信息
 */
export const getUserGameSessionInfo = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('web3_auth')
      .select('game_session_count')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('获取用户游戏次数信息失败:', error);
      return { 
        sessionCount: 0, 
        maxSessions: 3, 
        remainingSessions: 3 
      };
    }

    const sessionCount = data.game_session_count || 0;
    const maxSessions = 3;  // 最大游戏次数限制
    const remainingSessions = Math.max(0, maxSessions - sessionCount);

    return {
      sessionCount,
      maxSessions,
      remainingSessions
    };
  } catch (error) {
    console.error('获取用户游戏次数信息出错:', error);
    return { 
      sessionCount: 0, 
      maxSessions: 3, 
      remainingSessions: 3 
    };
  }
};

/**
 * 高级查询，获取NFTs并支持多条件查询和分页
 * @param {Object|string} options 查询选项或玩家ID字符串（兼容旧版本）
 * @returns {Promise<Object>} 包含NFT属性数据数组和分页信息的对象
 */
export const getNFTs = async (options = {}) => {

    // 只查询必要的字段：id、player_id、session_id、contract_address、attributes、agent
    // 不查询ai_analysis字段（数据量较大）
    let query = supabase.from('nft_attributes').select('id, player_id, session_id, contract_address, attributes, agent, created_at', { count: 'exact' });
    
    // 兼容旧版本调用方式，如果options是字符串，则视为playerId
    if (typeof options === 'string') {
      query = query.eq('player_id', options);
    } 
    // 新版本调用方式，options是对象，包含各种筛选条件
    else if (typeof options === 'object') {
      const { 
        playerId, 
        sessionId, 
        contractAddress,
        attributes,
        agent,
        aiAnalysis, // 保留参数但实际上不使用
        sorting = { column: 'created_at', ascending: false },
        // 分页参数
        page = 1,
        pageSize = 20
      } = options;
      
      // 应用条件过滤
      if (playerId) {
        query = query.eq('player_id', playerId);
      }
      
      if (sessionId) {
        query = query.eq('session_id', sessionId);
      }
      
      if (contractAddress) {
        query = query.eq('contract_address', contractAddress);
      }
      
      // 支持JSONB属性查询
      if (attributes && typeof attributes === 'object') {
        Object.entries(attributes).forEach(([key, value]) => {
          query = query.contains('attributes', { [key]: value });
        });
      }
      
      // 支持JSONB代理属性查询
      if (agent && typeof agent === 'object') {
        Object.entries(agent).forEach(([key, value]) => {
          query = query.contains('agent', { [key]: value });
        });
      }
      
      // 只有在明确指定需要ai_analysis时才查询
      if (aiAnalysis && options.includeAiAnalysis) {
        query = query.ilike('ai_analysis', `%${aiAnalysis}%`);
      }
      
      // 应用排序
      query = query.order(sorting.column, { ascending: sorting.ascending });
      
      // 应用分页
      const startIndex = (page - 1) * pageSize;
      query = query.range(startIndex, startIndex + pageSize - 1);
    }
    
    // 执行查询
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    // 处理返回的数据，确保属性格式正确
    const processedData = data.map(item => {
      // 确保attributes和agent是对象格式
      if (typeof item.attributes === 'string') {
        try {
          item.attributes = JSON.parse(item.attributes);
        } catch (e) {
          console.warn('attributes JSON:', e);
        }
      }
      
      if (typeof item.agent === 'string') {
        try {
          item.agent = JSON.parse(item.agent);
        } catch (e) {
          console.warn('agent JSON:', e);
        }
      }
      
      return item;
    });
    
    return processedData;
};


/**
 * 获取玩家胜率信息
 * @param {string} playerId 玩家ID
 * @returns {Promise<Object>} 胜率数据
 */
export const getPlayerWinRate = async (playerId) => {
  const { data, error } = await supabase
  .from('player_win_rates')
  .select('win_rate_display')
  .eq('player_id', playerId)
  .single();

  if (error) {
    console.log('获取玩家胜率信息失败:', error);
    // 返回一个默认的胜率数据而不是错误
    return { win_rate_display: "0%" };
  }

  return data;
};


/**
 * 获取所有用户胜率排行榜
 * @param {string} limit 返回结果数量限制
 * @returns {Promise<Object>} 胜率数据
 */
export const getWinRateTop = async (limit) => {
  limit = Math.min(limit, 100);
    const { data, error } = await supabase
    .from('player_win_rates')
    .select('player_id, wins, draws, losses, confidence_level, win_rate_display, total_rounds, user_points')
    .order('win_rate_display', { ascending: false })
    .limit(limit);

  if (error) {
    console.log('获取胜率排行榜失败:', error);
    // 返回一个空数组而不是错误
    return [];
  }

  return data;
};

/**
 * 获取玩家游戏记录列表
 * @param {string} playerId 玩家ID
 * @param {Object} options 查询选项，如分页和状态过滤
 * @returns {Promise<Array>} 游戏记录列表
 */
export const getPlayerGameRecords = async (playerId, options = {}) => {
  try {
    const { page = 1, pageSize = 10, status } = options;
    const startIndex = (page - 1) * pageSize;
    
    // 使用新的数据库函数获取玩家游戏记录
    let query = supabase
      .rpc('get_player_game_sessions', { player_id_param: playerId })
      .select('*', { count: 'exact' })
      .order('session_created_at', { ascending: false });
    
    // 如果指定了状态，添加状态过滤
    if (status) {
      query = query.eq('status', status);
    }
    
    // 应用分页
    query = query.range(startIndex, startIndex + pageSize - 1);
    
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    // 处理结果，转换数据结构以兼容前端
    const processedData = data.map(session => ({
      session_id: session.session_id,
      player_id: session.player_id,
      created_at: session.session_created_at,
      updated_at: session.session_updated_at,
      total_rounds: session.total_rounds,
      current_round: session.current_round,
      player_score: session.player_score,
      ai_score: session.ai_score,
      player_cooperation_rate: session.player_cooperation_rate,
      ai_cooperation_rate: session.ai_cooperation_rate,
      status: session.status,
      // 根据得分计算游戏结果
      outcome: session.player_score > session.ai_score ? '胜利' : 
               session.player_score < session.ai_score ? '失败' : '平局'
    }));
    
    return { 
      data: processedData, 
      count, 
      totalPages: Math.ceil(count / pageSize), 
      currentPage: page 
    };
  } catch (error) {
    console.error('获取玩家游戏记录列表失败:', error);
    return { data: [], count: 0, totalPages: 0, currentPage: 1 };
  }
};
